import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent, MediaPreviewComponent, UtilityService, PreviewMode, OrganizationService } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Asset, AssetDomain, AssetGroup, AssetTag, AssetTagService, AssetsDomainService, AssetsService, AssetDetails, OfferTag, OfferTagService } from 'src/app/modules/standard/v1';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import * as dayjs from 'dayjs';
import { CustomFieldDefinition, CustomFieldType } from '../../models/custom-field.model';
import { CustomFieldService } from '../../services/custom-field.service';
import { DatePipe } from '@angular/common';

const log = new Logger('AddAssetComponent');
const urlRegex = '^https?:\\/\\/((www\\.)?([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\\/[a-zA-Z0-9@:%_\\+.~#?&//= \\-\\(\\)]*)?(\\?[a-zA-Z0-9&=\\-\\+%]*)?)$';
@Component({
  selector: 'app-asset-import',
  templateUrl: './add-asset.component.html',
  styleUrls: ['./add-asset.component.scss']
})

export class AddAssetComponent<Tag extends OfferTag, ATag extends AssetTag, Group extends AssetGroup> implements OnInit, OnDestroy {
  assets$: Observable<any> = of({} as any);
  assetsDetails!: AssetDetails;
  currentAsset: any;
  assetGroups$: any[] = [] as any[];
  tagCtrl = new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(50)]);
  assetOfferTags: Tag[] = [];
  assetOfferTagIds: string[] = [];
  updatedAssetOfferTagIds: string[] = [];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  PreviewMode = PreviewMode;
  customFields$: Observable<CustomFieldDefinition[]> = of([]);
  customFieldValues: any = {};
  dateFieldsInCustomFields: string[] = [];
  selectFieldsInCustomFields: string[] = [];

  formGroup = new FormGroup({
    detail: new FormGroup({
      CustomFields: new FormGroup({}),
      clientKey: new FormControl<string | null>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      assetName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      fileName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      assetGroup: new FormControl(null, [Validators.required]),
      dateAdded: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100)]),
      id: new FormControl<string>('0', { nonNullable: true }),
      description: new FormControl<string>(''),
      additionalSpecs: new FormControl<string>(''),
      dateShot: new FormControl<Date | string | null>(null),
      dateCreated: new FormControl<Date | string | null>({ value: null, disabled: true }),
      originalFileName: new FormControl<string | null>(''),
      userNameModified: new FormControl<string>(''),
      dateFileModified: new FormControl<Date | string | null>(null),
      colorChannel: new FormControl<string | null>(''),
      fileType: new FormControl<string | null>('', Validators.required),
      assetType: new FormControl<string | null>(''),
      imageSource: new FormControl<string | null>(''),
      deleteStatus: new FormControl<boolean>(false),
      doNotUse: new FormControl<boolean>(false),
      expiryDate: new FormControl<Date | string | null>(null),
      embargoDate: new FormControl<Date | string | null>(null),
      keywords: this.fb.array([]),
      restrictions: new FormControl<string>(''),
      stockSourceInformation: new FormControl<string>(''),
      webUrl: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.pattern(urlRegex)] }),
      printUrl: new FormControl<string>('', { nonNullable: true, validators: [Validators.pattern(urlRegex)] }),
    })
  });

  groupId: string = '0';
  assetId: string = '0';
  assetType: string = '';
  assetGroupName: string = '';
  assetUrl: string = '';
  isMontage: boolean = false;
  previewUrl: string = './assets/images/default-image.png';
  private destroy$ = new Subject<void>();
  private lastAutoFilledUrl: string | null = null;

  constructor(private router: Router,
    private route: ActivatedRoute,
    private assetDomainService: AssetsDomainService<AssetDomain>,
    private assetTagService: AssetTagService<ATag, Group>,
    private offerTagService: OfferTagService<Tag>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private customFieldService: CustomFieldService,
    public utilityService: UtilityService,
    private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.assetId = '0';
    this.initializeDateAdded();
    this.loadAssetGroups();
    this.setupAssetGroupListener();
    this.setupWebUrlListener();
    this.loadCustomFields();
    this.addAdditionalText('');
  }

  private initializeDateAdded(): void {
    const currentDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy') || '';
    this.formGroup.controls.detail.controls.dateAdded.setValue(currentDate);
  }

  private loadAssetGroups(): void {
    this.assetTagService.assetGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.assetGroups$ = data;
        },
        error: (error) => {
          log.error('Error loading asset groups', error);
        }
      });
  }

  private setupAssetGroupListener(): void {
    this.formGroup.controls.detail.controls.assetGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((groupId: string | null) => {
        if (!groupId) {
          this.assetGroupName = '';
          return;
        }
        const selectedGroup = this.assetGroups$.find(g => g.Id === groupId);
        if (selectedGroup) {
          this.assetGroupName = selectedGroup.AssetGroupName;
        }
      });
  }

  private setupWebUrlListener(): void {
    const webUrlControl = this.formGroup.controls.detail.controls.webUrl;

    webUrlControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter(url => !!url && this.isValidUrl(url)),
        switchMap(url => {
          this.previewUrl = url;
          const fileName = this.extractFileNameFromUrl(url);

          if (!fileName) return of(null);

          return this.assetDomainService.getMimeTypeFromUrl(url)
            .pipe(map(mimeType => ({ url, fileName, mimeType })));
        })
      )
      .subscribe(result => {
        if (!result) return;

        const { url, fileName, mimeType } = result;
        const detail = this.formGroup.controls.detail.controls;

        const isNewUrl = this.lastAutoFilledUrl !== url;
        this.lastAutoFilledUrl = url;

        const patch: any = {};

        if (isNewUrl || !detail.fileName.dirty)
          patch.fileName = fileName;

        if (isNewUrl || !detail.assetName.dirty)
          patch.assetName = this.removeExtension(fileName);

        if (isNewUrl || !detail.clientKey.dirty)
          patch.clientKey = this.removeExtension(fileName);

        if (isNewUrl || !detail.fileType.dirty)
          patch.fileType = mimeType;

        if (isNewUrl || !detail.dateAdded.dirty)
          patch.dateAdded = this.datePipe.transform(new Date(), 'MM/dd/yyyy');

        this.formGroup.controls.detail.patchValue(patch, { emitEvent: false });
      });
  }

  isValidUrl(value: string): boolean {
    try {
      if (!value) return false;

      const validUrl = new URL(value);
      return validUrl.host !== "" && validUrl.origin !== "null";
    } catch {
      return false;
    }
  }

  private extractFileNameFromUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      const path = parsedUrl.pathname;

      if (!path) {
        return null;
      }

      const segments = path.split('/').filter(Boolean);
      const lastSegment = segments[segments.length - 1];

      return lastSegment ? decodeURIComponent(lastSegment) : null;
    } catch {
      return null;
    }
  }

  private removeExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '');
  }

  getOfferTagsByIds(tags: string[]) {
    if (tags.length) {
      this.offerTagService
        .GetOfferTagsByIds(tags)
        .subscribe((offerTags: any) => {
          this.assetOfferTags = offerTags;
        });
    }
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }

  openUrl(url: any) {
    if (url) {
      const value = this.validateUrl(url);
      if (value == null) {
        window.open(url, '_blank');
      }
    }
  }

  validateUrl(value: any) {
    let valid = true;
    try {
      if (value) {
        let validUrl = new URL(value);
        if (validUrl.host == "" && validUrl.origin == "null") {
          valid = false;
        }
      }
    } catch {
      valid = false;
    }
    return valid ? null : { invalidUrl: true };
  }

  private loadCustomFields(): void {
    this.customFields$ = this.customFieldService.getCustomFieldDefinitionsByVertexType('ASSET').pipe(
      tap((fields: CustomFieldDefinition[]) => {
        // Get the CustomFields FormGroup
        const customFieldsGroup = this.formGroup.controls.detail.controls.CustomFields as FormGroup;

        // Create FormControls for each custom field
        fields.forEach((field: CustomFieldDefinition) => {
          if (!customFieldsGroup.get(field.FieldName)) {
            const formControl = new FormControl<any>({ value: null, disabled: field.IsReadOnly });
            if (field.FieldType === CustomFieldType.Toggle || field.FieldType === CustomFieldType.Checkbox) {
              formControl.setValue(false);
            }
            customFieldsGroup.addControl(field.FieldName, formControl);
            if(field.FieldType === CustomFieldType.Date) {
              this.dateFieldsInCustomFields.push(field.FieldName);
            }
            if(field.FieldType === CustomFieldType.MultiSelect) {
              this.selectFieldsInCustomFields.push(field.FieldName);
            }
          }
        });
        if (Object.keys(this.customFieldValues).length > 0) {
          Object.keys(this.customFieldValues).forEach(key => {
            if (customFieldsGroup.get(key)) {
              if(this.dateFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                customFieldsGroup.get(key)?.setValue(new Date(this.customFieldValues[key]));
              } else if(this.selectFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                customFieldsGroup.get(key)?.setValue(typeof this.customFieldValues[key] === 'string' ? [this.customFieldValues[key]] : this.customFieldValues[key]);
              } else {
                customFieldsGroup.get(key)?.setValue(this.customFieldValues[key]);
              }
            }
          });
        }
      })
    );
  }

  getCustomFieldsGroup(): FormGroup {
    return this.formGroup.controls.detail.controls.CustomFields as FormGroup;
  }

  save() {
    this.trimControlValues(this.formGroup);

    if (this.formGroup.valid) {
      let assets = this.formGroup.getRawValue() as any;
      assets.OfferTagIds = this.updatedAssetOfferTagIds;
      const dateFileModified = this.formatDate(
        this.formGroup.controls.detail.controls.dateFileModified.value
      );
      const dateShot = this.formatDate(
        this.formGroup.controls.detail.controls.dateShot.value
      );
      const embargoDate = this.formatDate(
        this.formGroup.controls.detail.controls.embargoDate.value
      );
      const expiryDate = this.formatDate(
        this.formGroup.controls.detail.controls.expiryDate.value
      );
      const dateCreated = this.formatDate(
        this.formGroup.controls.detail.controls.dateCreated.value
      );
      const customFields = Object.keys(this.formGroup.controls.detail.controls.CustomFields.controls);
      if (customFields && customFields.length > 0) {
        customFields.forEach((fieldName) => {
          if(this.dateFieldsInCustomFields.includes(fieldName) && this.formGroup.controls.detail.controls.CustomFields.get(fieldName)?.value) {
            assets.detail.CustomFields[fieldName] = this.formatDate(this.formGroup.controls.detail.controls.CustomFields.get(fieldName)?.value);
          }
        });
      }

      const additionaltexts = assets.detail.keywords && assets.detail.keywords.length > 0
        ? assets.detail.keywords.filter(
          (i: any) => i.addtionalTextDetail
        ).map((x: any) => x.addtionalTextDetail) : [];
      const dataToPass = {
        OfferTagIds: assets.OfferTagIds,
        Detail: {
          CustomFields: assets.detail.CustomFields,
          FileName: assets.detail?.fileName,
          AssetName: assets.detail?.assetName,
          DateAdded: assets.detail?.dateAdded,
          ClientKey: assets.detail?.clientKey,
          Description: assets.detail?.description,
          AdditionalSpecs: assets.detail?.additionalSpecs,
          DateShot: dateShot,
          OriginalFileName: assets.detail?.originalFileName,
          DateCreated: dateCreated,
          UserNameModified: assets.detail?.userNameModified,
          DateFileModified: dateFileModified,
          ColorChannel: assets.detail?.colorChannel,
          FileType: assets.detail?.fileType,
          AssetType: assets.detail?.assetType,
          ImageSource: assets.detail?.imageSource,
          DeleteStatus: assets.detail?.deleteStatus,
          DoNotUse: assets.detail?.doNotUse,
          ExpiryDate: expiryDate,
          EmbargoDate: embargoDate,
          Restrictions: assets.detail?.restrictions,
          StockSourceInformation: assets.detail?.stockSourceInformation,
          WebUrl: assets.detail?.webUrl,
          PrintUrl: assets.detail?.printUrl,
          Id: "",
          Keywords: additionaltexts,
        },
        AssetGroupId: assets.detail.assetGroup,
        AssetGroupName: this.assetGroupName
      }

      this.assetDomainService.createAsset(dataToPass).subscribe({
        next: (response: any) => {
          this.matSnackBar.open(`${this.formGroup.controls.detail.controls.assetName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.assetId = response.DomainId;
          this.router.navigate([`../${response.DomainId}`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving asset', error);
            this.matSnackBar.open('500 Error saving asset', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          if (error.status === 400) {
            const apiValidations = error.error;
            if (Array.isArray(apiValidations)) {
              const errorMessages: string[] = [];
              apiValidations.forEach((validation: any) => {
                if (!validation?.PropertyName) {
                  errorMessages.push(validation?.ErrorMessage);
                  return;
                }
                const controlPath = validation.PropertyName.split('.').map((segment: string) => segment.charAt(0).toLowerCase() + segment.slice(1)).join('.');
                const control = this.formGroup?.get(controlPath);
                if (control) {
                  control.markAsTouched();
                  control.setErrors({
                    ...(control.errors || {}),
                    apiError: validation.ErrorMessage
                  });
                } else {
                  errorMessages.push(validation.ErrorMessage);
                }
              });

              if (errorMessages.length > 0) {
                this.matSnackBar.open(errorMessages[0], 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
              }
              this.formGroup.updateValueAndValidity();
            }
            else if (apiValidations?.error?.message) {
              this.matSnackBar.open(apiValidations.error.message, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else if (apiValidations?.message) {
              this.matSnackBar.open(apiValidations.message, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else if (typeof apiValidations === 'string') {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else {
              this.matSnackBar.open(typeof apiValidations === 'object' && apiValidations?.Message ? apiValidations.Message : apiValidations, 'Error',
                {
                  verticalPosition: 'top',
                  panelClass: ['snackbar-error']
                }
              );
            }
          }
        },
      });
    }
  }

  formatDate(date: Date | string | null) {
    if (date) {
      const newDate = dayjs(date).format('MM/DD/YYYY');
      return newDate;
    } else {
      return '';
    }
  }

  public get additionalTexts() {
    return this.formGroup.controls['detail']?.controls['keywords'] as FormArray;
  }

  addAdditionalText(datatoAdd: string) {
    const formtoAdd = this.fb.group({
      addtionalTextDetail: [datatoAdd],
    });
    this.additionalTexts.push(formtoAdd);
  }

  deleteAddtionalText(index: number) {
    if (this.additionalTexts.controls.length > 1) {
      this.additionalTexts.removeAt(index);
    }
  }

  reloadCurrentRoute() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    });
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  tagSelected(tagId: string): void {
    if (this.updatedAssetOfferTagIds?.length > 0 && this.updatedAssetOfferTagIds.includes(tagId)) {
      return;
    }
    this.updatedAssetOfferTagIds.push(tagId);
  }

  addTag(offerTag: OfferTag): void {
    this.updatedAssetOfferTagIds.push(offerTag.Id);
    this.matSnackBar.open(`${offerTag.OfferTagName} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  removeTag(tagId: string): void {
    this.updatedAssetOfferTagIds = this.updatedAssetOfferTagIds.filter(id => id !== tagId);
  }

  toCamel(o: any) {
    var newO, origKey, newKey, value
    if (o instanceof Array) {
      return o.map((value) => {
        if (typeof value === "object") {
          value = this.toCamel(value)
        }
        return value
      })
    } else {
      newO = {} as any;
      for (origKey in o) {
        if (o.hasOwnProperty(origKey)) {
          newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString()
          value = o[origKey]
          if (value instanceof Array || (value !== null && value.constructor === Object)) {
            value = this.toCamel(value)
          }
          newO[newKey] = value
        }
      }
    }
    return newO
  }

  openPreviewDialog(): void {
    this.assetsDetails = {
      FileName: this.formGroup.controls.detail.controls.fileName.value ?? '',
      AssetName: this.formGroup.controls.detail.controls.assetName.value ?? '',
      DateAdded: this.formGroup.controls.detail.controls.dateAdded.value ?? '',
      Type: this.formGroup.controls.detail.controls.fileType.value ?? '',
      FileType: this.formGroup.controls.detail.controls.fileType.value ?? undefined,
      AssetType: this.formGroup.controls.detail.controls.assetType.value ?? undefined,
      ClientKey: this.formGroup.controls.detail.controls.clientKey.value ?? '',
      WebUrl: this.formGroup.controls.detail.controls.webUrl.value ?? ''
    };

    if (!this.assetsDetails.FileType?.trim()) {
      this.matSnackBar.open('File Type is required for preview', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
      return;
    }

    let urlToPass = '';
    let videoCaptionsUrl = '';
    if (this.assetsDetails && this.assetsDetails.FileType?.includes('x-zip') && this.assetsDetails.ZipConfig && this.assetsDetails.ZipType && this.assetsDetails.ZipType.includes('Video')) {
      let zipConfigInfo = JSON.parse(this.assetsDetails.ZipConfig);
      urlToPass = zipConfigInfo.PrimaryFileUrl;
      videoCaptionsUrl = zipConfigInfo.CaptionsFileUrl;
    } else if (this.assetsDetails.FileType?.includes('x-zip') && !this.assetsDetails?.ZipType?.includes('Video')) {
      urlToPass = '../../../../assets/images/mp4.png'
    }
    else {
      urlToPass = this.assetsDetails.WebUrl as string;
    }
    const dialogRef = this.dialog.open(MediaPreviewComponent, {
      width: '600px',
      data: { name: this.assetsDetails.AssetName, url: this.assetsDetails.FileName, type: this.assetsDetails.FileType, assetUrl: urlToPass, videoCaptionsUrl: videoCaptionsUrl },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

